// DashboardA.js — Dashboard de Administrador (Hidrometrics)
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext/AuthContext";
import Topbar from "../Topbar/Topbar";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import ChatWidget from "../Chat/ChatWidget";
import "./DashboardA.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/* ════════════════════════════════════════════════════════════════════
   HELPERS
   ════════════════════════════════════════════════════════════════════ */
const todayYMD = () => {
  const d = new Date(),
    p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const toYMD = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const fmt0 = (v) => Math.round(parseFloat(v) || 0);
const fmt1 = (v) => (parseFloat(v) || 0).toFixed(1);

/* ── Siempre 48 periodos ──────────────────────────────────────────
   Ya NO hay resoluciones discretas (hour/3h/day/week/month) — el
   backend (buildNBuckets) divide [start,end] en 48 periodos de
   duración IGUAL sin importar el rango, y regresa las etiquetas ya
   formateadas (HH:MM si es sub-diario, "DD Mon" si son varios días,
   "Mon AAAA" si es largo). El frontend solo necesita un placeholder de
   48 casillas vacías mientras llega la respuesta real. */
const NUM_BUCKETS = 48;

const buildPlaceholderSeries = () =>
  Array.from({ length: NUM_BUCKETS }, () => ({
    label: "",
    hasData: false,
    hasLevelData: false,
    tankStart: 0,
    captured: 0,
    dispensed: 0,
    unmetered: 0,
    overflow: 0,
    tankEnd: 0,
    rainOffered: 0,
    rainCaptured: 0,
    rainfall_mm: 0,
    tankAvgPct: null,
    tankAvgVolumeL: null,
  }));

/* Duración aproximada de cada uno de los 48 periodos, para el badge que
   antes decía "Vista por hora/semana/mes" — ahora se calcula en vivo
   según el rango seleccionado, en vez de un puñado de valores fijos.
   Esto es solo un ESTIMADO para el placeholder mientras carga; el valor
   EXACTO (granularidad "lógica" real que eligió el backend) llega en
   la respuesta y se formatea con formatBucketMinutes más abajo. */
const bucketDurationLabel = (start, end) => {
  if (!start || !end) return "";
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T23:59:59.999`);
  const totalMs = Math.max(1, e - s);
  const bucketMin = totalMs / NUM_BUCKETS / 60000;
  if (bucketMin < 60) return `~${Math.round(bucketMin)} min por periodo`;
  const bucketH = bucketMin / 60;
  if (bucketH < 24)
    return `~${bucketH % 1 === 0 ? bucketH : bucketH.toFixed(1)} h por periodo`;
  const bucketDays = bucketH / 24;
  return `~${bucketDays % 1 === 0 ? bucketDays : bucketDays.toFixed(1)} días por periodo`;
};

/* Formato EXACTO de la granularidad real que eligió el backend (siempre
   uno de los valores "lógicos" de NICE_BUCKET_MINUTES en index.js) — sin
   "~", ya no es un estimado. Reconoce múltiplos exactos de 7 días como
   semanas (7d/14d/21d/28d), igual que los ejemplos que dio Fernando. */
const formatBucketMinutes = (m) => {
  if (m == null) return "";
  if (m < 60) return `${m} min por periodo`;
  if (m < 1440) return `${m / 60} h por periodo`;
  const days = m / 1440;
  if (Number.isInteger(days) && days % 7 === 0 && days <= 28) {
    const weeks = days / 7;
    return `${weeks} semana${weeks > 1 ? "s" : ""} por periodo`;
  }
  if (Number.isInteger(days)) return `${days} día${days > 1 ? "s" : ""} por periodo`;
  return `${days.toFixed(1)} días por periodo`;
};

/* ── Estados del sistema con iconos y lenguaje de usuario ──────────
   Cadena lineal alineada al firmware real de Alfredo (getOperationalState):
   Dry/Idle → Rain Detected → First Flush Active → First Flush Complete →
   Harvesting Active → Tank Overflow → Filter Check.
   (El firmware NO emite "Tank Full" ni "Reset Needed" por separado.)        */
const OP_STATES_B = [
  { key: "Dry / Idle", label: "En espera", icon: "⏸" },
  { key: "Rain Detected", label: "Lluvia detectada", icon: "🌧" },
  {
    key: "First Flush Active",
    label: "Separando primeras lluvias",
    icon: "🔀",
  },
  { key: "First Flush Complete", label: "Listo para cosechar", icon: "✅" },
  { key: "Harvesting Active", label: "Cosechando agua", icon: "💧" },
  { key: "Tank Overflow", label: "Tanque lleno / Rebosando", icon: "🛢" },
  { key: "Filter Check", label: "Revisar filtro", icon: "🔧" },
];

/* Estados especiales fuera de la cadena lineal (no son pasos del progreso).
   El firmware los emite cuando el vector WPS es inconsistente o transitorio. */
const SPECIAL_STATES = {
  "Fault State": { label: "Falla de sensor", icon: "⚠️" },
  "Unclassified / Transition": { label: "Estado transitorio", icon: "🔄" },
};

/* Etiqueta legible para cualquier estado del firmware (lineal o especial) */
const stateLabel = (key) =>
  OP_STATES_B.find((s) => s.key === key)?.label ||
  SPECIAL_STATES[key]?.label ||
  key ||
  "Sin datos";

const getSiguienteAccion = (opState, tankLevel) => {
  if (tankLevel >= 80)
    return "Usa o transfiere agua para liberar capacidad antes de la próxima lluvia.";
  switch (opState) {
    case "Dry / Idle":
      return "Tu sistema está en espera. Listo para el próximo evento de lluvia.";
    case "Rain Detected":
      return "Lluvia detectada. El sistema procesará el primer escurrimiento.";
    case "First Flush Active":
      return "Separando primeras lluvias para proteger la calidad del agua cosechada.";
    case "First Flush Complete":
      return "El sistema está listo para cosechar agua limpia.";
    case "Harvesting Active":
      return "Tu sistema está operando correctamente.";
    case "Tank Overflow":
      return "Tanque lleno. Usa o transfiere agua para no perder la próxima lluvia.";
    case "Filter Check":
      return "Posible obstrucción del filtro del techo. Revisa el filtro.";
    case "Fault State":
      return "Lectura de sensores inconsistente. Revisa la instalación de los WPS.";
    case "Unclassified / Transition":
      return "El sistema está en transición entre estados. Esperando que los sensores se estabilicen.";
    default:
      return "Sistema listo.";
  }
};

const getEstadoColor = (opState) => {
  if (!opState) return "#95a5a6";
  if (["Filter Check", "Fault State"].includes(opState)) return "#e74c3c";
  if (opState === "Tank Overflow") return "#e67e22";
  if (opState === "Harvesting Active") return "#27ae60";
  if (
    ["Rain Detected", "First Flush Active", "First Flush Complete"].includes(
      opState,
    )
  )
    return "#3498db";
  return "#95a5a6";
};

const PIE_COLORS = ["#0064d2", "#64b5f6", "#e74c3c", "#9b59b6"];

/* ── Leyenda HTML propia — reemplaza el <Legend> de recharts en las 3
   gráficas. Motivo: el legend SVG de recharts, al envolver a 2 líneas en
   pantallas angostas, podía encimarse con las etiquetas del eje X (bug
   reportado). Al ser un elemento HTML normal con flex-wrap, nunca se
   superpone con el SVG del chart — siempre empuja el layout hacia abajo. */
const ChartLegend = ({ items }) => (
  <div className="db-chart-legend">
    {items.map((it) => {
      const cls = `db-chart-legend-dot${
        it.dashed ? " db-chart-legend-dot--dashed" : it.outline ? " db-chart-legend-dot--outline" : ""
      }`;
      const style = it.outline
        ? { background: `${it.color}2e`, borderColor: it.color } // 2e hex ≈ 18% opacidad, igual que la barra
        : { background: it.dashed ? "transparent" : it.color, borderColor: it.color };
      return (
        <span key={it.label} className="db-chart-legend-item">
          <span className={cls} style={style} />
          {it.label}
        </span>
      );
    })}
  </div>
);

/* ── Rachas de bloques que cumplen un predicado (reutilizable para huecos,
   tanto en estas 2 gráficas como en la nueva mini-gráfica de nivel) ──── */
const computeRuns = (d2, predicate) => {
  const runs = [];
  let inRun = false,
    runStart = null;
  d2.forEach((d, i) => {
    const match = predicate(d);
    if (match && !inRun) {
      inRun = true;
      runStart = d.label;
    } else if (!match && inRun) {
      inRun = false;
      runs.push({ x1: runStart, x2: d2[i - 1]?.label });
    }
  });
  if (inRun && runStart) runs.push({ x1: runStart, x2: d2[d2.length - 1]?.label });
  return runs;
};

// Huecos sin telemetría — sombreado gris. La distinción "interpolado vs.
// arrastrado" ahora solo es relevante para la mini-gráfica de nivel del
// tanque (más abajo), ya que estas 2 gráficas ya no muestran el nivel.
const computeUnknownGaps = (d2) => computeRuns(d2, (d) => !d.hasData);

// Ancho fijo (px) de las barras superpuestas cosechada/utilizada — junto
// con barGap={-OVERLAP_BAR_SIZE} en el ComposedChart, hace que ambas
// barras nazcan en el mismo x (offset = barSize + barGap = 0). Es un
// número ajustable: más chico = más espacio visible entre categorías,
// más grande = barras más anchas.
const OVERLAP_BAR_SIZE = 10;

// Color de "Agua no medida" (salida de agua que el caudalímetro no contó).
const UNMETERED_COLOR = "#f57c00";

/* ════════════════════════════════════════════════════════════════════
   CHART 1 — DOS BANDAS SINCRONIZADAS
   ════════════════════════════════════════════════════════════════════ */
const TwoBandChart = ({ data, tankCapacity }) => {
  // Las barras siempre ocupan el 100% del ancho de su categoría, sin espacio
  // entre ellas (barCategoryGap="0%", barGap={0}, sin maxBarSize) — así se
  // ven consistentes sin importar el rango seleccionado. Ya no hay
  // resoluciones discretas: SIEMPRE son 48 periodos (ver NUM_BUCKETS), así
  // que el eje X ya no necesita la lógica de "grupos" (mes/semana como
  // super-etiqueta) — las 48 etiquetas ya vienen listas del backend.

  const d2 = data.map((d) => {
    const hasData = d.hasData !== false;
    return {
      label: d.label,
      hasData,
      // lluvia y oferta: null si no hay datos, para que las líneas no se
      // dibujen "en cero" durante los huecos sombreados
      lluvia: hasData ? parseFloat(d.rainfall_mm || 0) : null,
      oferta: hasData ? fmt0(d.rainOffered || 0) : null,
      cosechada: fmt0(d.captured || 0),
      // "Agua utilizada" es BARRA, lado a lado con cosechada (ya NO
      // apilada) — dos barras por periodo, como pidió Fernando.
      aprovechada: fmt0(Math.abs(d.dispensed || 0)),
      // "Agua no medida": litros que bajaron del tanque sin pasar por el
      // caudalímetro (la calcula el backend, ver buildNBuckets). Tercera
      // barra, anaranjada, superpuesta con las otras dos.
      noMedida: fmt0(Math.abs(d.unmetered || 0)),
    };
  });

  const unknownGaps = computeUnknownGaps(d2);

  // Dominio COMPARTIDO del eje "vol" — ambas gráficas superpuestas deben
  // usar el mismo rango para que 100L se vea con la misma altura en las
  // dos, sin importar cuál de las dos series sea más grande ese periodo.
  const volMax = Math.max(
    1,
    ...d2.map((d) => Math.max(d.cosechada || 0, d.aprovechada || 0, d.noMedida || 0)),
  );
  const volDomain = [0, Math.ceil(volMax * 1.05)];

  // Tooltip propio para la banda de Litros superpuesta — vive en la
  // gráfica de ATRÁS (cosechada), que es la que recibe el hover ya que
  // la de adelante tiene pointer-events:none. Muestra ambos valores
  // aunque cada uno viva en una gráfica distinta.
  const VolTooltip = ({ active, label }) => {
    if (!active) return null;
    const point = d2.find((d) => d.label === label);
    if (!point) return null;
    return (
      <div className="db-vol-tooltip">
        <div className="db-vol-tooltip-label">{label}</div>
        <div className="db-vol-tooltip-row">
          <span className="db-vol-tooltip-dot" style={{ background: "#0064d2" }} />
          Cosechada: <strong>{point.cosechada} L</strong>
        </div>
        <div className="db-vol-tooltip-row">
          <span className="db-vol-tooltip-dot" style={{ background: "#1abc9c" }} />
          Utilizada: <strong>{point.aprovechada} L</strong>
        </div>
        <div className="db-vol-tooltip-row">
          <span className="db-vol-tooltip-dot" style={{ background: UNMETERED_COLOR }} />
          No medida: <strong>{point.noMedida} L</strong>
        </div>
      </div>
    );
  };

  // El número de categorías ya NO es fijo (>=48, puede ser más según la
  // duración lógica elegida) — el intervalo se adapta para mostrar
  // siempre ~12 etiquetas visibles, sin importar cuántos bloques haya.
  const TICK_INTERVAL = Math.max(1, Math.floor(d2.length / 12));

  const legendTop = [
    { label: "Lluvia (mm)", color: "#1565c0" },
    { label: "Oferta pluvial (L)", color: "#29b6f6" },
  ];
  const legendBottom = [
    { label: "Agua cosechada (L)", color: "#0064d2" },
    { label: "Agua utilizada (L)", color: "#1abc9c", outline: true },
    { label: "Agua no medida (L)", color: UNMETERED_COLOR, outline: true },
  ];

  return (
    <div className="db-twoband">
      <div className="db-band-label-row">
        <span
          className="db-band-ytitle db-band-ytitle--left"
          style={{ color: "#1565c0" }}
        >
          Lluvia (mm)
        </span>
        <span
          className="db-band-ytitle db-band-ytitle--right"
          style={{ color: "#29b6f6" }}
        >
          Oferta pluvial (L)
        </span>
      </div>
      <ResponsiveContainer width="100%" height={150}>
        <ComposedChart
          data={d2}
          syncId="db-band"
          barCategoryGap="0%"
          barGap={0}
          margin={{ top: 4, right: 52, bottom: 4, left: 48 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#eaecf4"
          />
          {unknownGaps.map((g, i) => (
            <ReferenceArea
              key={`gap-top-${i}`}
              yAxisId="mm"
              x1={g.x1}
              x2={g.x2}
              fill="rgba(200,215,235,0.22)"
              stroke="none"
            />
          ))}
          <XAxis dataKey="label" hide />
          <YAxis
            yAxisId="mm"
            reversed
            tickFormatter={(v) => `${v}`}
            tick={{ fontSize: 11, fill: "#1565c0" }}
            tickLine={false}
            axisLine={false}
            width={48}
          />
          <YAxis
            yAxisId="l"
            orientation="right"
            reversed
            tickFormatter={(v) => `${v}L`}
            tick={{ fontSize: 11, fill: "#29b6f6" }}
            tickLine={false}
            axisLine={false}
            width={48}
          />
          <Tooltip
            formatter={(val, name, props) => {
              const isRain = props?.dataKey === "lluvia";
              return [
                isRain ? `${val} mm` : `${val} L`,
                isRain ? "Lluvia" : "Oferta pluvial",
              ];
            }}
          />
          <Bar
            yAxisId="mm"
            dataKey="lluvia"
            name="Lluvia (mm)"
            fill="#1565c0"
            fillOpacity={1}
            radius={[0, 0, 3, 3]}
          />
          <Line
            yAxisId="l"
            type="linear"
            dataKey="oferta"
            name="Oferta pluvial (L)"
            stroke="#29b6f6"
            strokeOpacity={0.45}
            strokeWidth={2.5}
            dot={{ r: 2, fill: "#29b6f6", fillOpacity: 0.45, strokeWidth: 0 }}
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <ChartLegend items={legendTop} />

      {/* Mini-gráfica de nivel del tanque — Fernando pidió sacarla de la
          gráfica de Litros y ponerla "como una tercera gráfica/card" entre
          las dos bandas existentes, sin alterar sus tamaños (150/230px
          intactos). Muestra el promedio de nivel real por cada uno de los
          mismos 48 periodos. */}
      <MiniTankChart data={data} tankCapacity={tankCapacity} />

      <div className="db-band-label-row" style={{ marginTop: "0.2em" }}>
        <span
          className="db-band-ytitle db-band-ytitle--left"
          style={{ color: "#7a8a9e" }}
        >
          Litros
        </span>
      </div>

      {/* banda volumen — cosechada y utilizada SUPERPUESTAS (no lado a
          lado, no apiladas). Recharts no soporta superposición nativa de
          barras por defecto: dos <Bar> sin stackId se reparten el ancho
          lado a lado; con el MISMO stackId se apilan una arriba de la
          otra desde donde termina la anterior. La técnica que sí funciona
          de forma confiable: ancho de barra FIJO (OVERLAP_BAR_SIZE) +
          barGap NEGATIVO igual a ese ancho — el offset entre barras
          consecutivas es barSize + barGap = 0, así que la segunda barra
          nace exactamente en el mismo x que la primera. Sin stackId, sin
          shape personalizado. */}
      <ResponsiveContainer width="100%" height={230}>
        <ComposedChart
          data={d2}
          syncId="db-band"
          barCategoryGap="0%"
          barGap={-OVERLAP_BAR_SIZE}
          margin={{ top: 4, right: 52, bottom: 20, left: 48 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#eaecf4"
          />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: "#7a8a9e" }}
            tickLine={false}
            axisLine={{ stroke: "#dde5f0" }}
            height={30}
            interval={TICK_INTERVAL}
          />
          <YAxis
            yAxisId="vol"
            domain={volDomain}
            tickFormatter={(v) => `${v}L`}
            tick={{ fontSize: 11, fill: "#7a8a9e" }}
            tickLine={false}
            axisLine={false}
            width={48}
          />
          {/* Eje derecho INVISIBLE — esta gráfica no necesita mostrar nada
              a la derecha, pero la de arriba (Lluvia/Oferta) SÍ tiene un
              eje real ahí (Oferta pluvial, 48px). Sin este eje "fantasma"
              reservando el mismo espacio, las 3 gráficas sincronizadas
              (syncId="db-band") terminan con áreas de dibujo de ancho
              distinto y las barras/líneas no alinean verticalmente entre
              sí, aunque los "margin" se vean iguales en el código.
              IMPORTANTE: no usar "hide" — puede colapsar el ancho
              reservado a 0 (ya nos pasó antes). width explícito +
              tick/axisLine/tickLine en false sí reserva el espacio sin
              dibujar nada. */}
          <YAxis
            yAxisId="volRight"
            orientation="right"
            width={48}
            tick={false}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<VolTooltip />} />

          {unknownGaps.map((g, i) => (
            <ReferenceArea
              key={`gap-${i}`}
              yAxisId="vol"
              x1={g.x1}
              x2={g.x2}
              fill="rgba(200,215,235,0.22)"
              stroke="none"
            />
          ))}
          {/* Ancho de barra FIJO + barGap NEGATIVO igual a ese ancho: la
              segunda barra nace exactamente donde nace la primera (offset
              = barSize + barGap = barSize - barSize = 0). Es la técnica
              estándar de Recharts para superponer barras — sin stackId,
              sin shape personalizado, solo aritmética de posición.

              Cosechada = relleno sólido. Utilizada = relleno CASI
              transparente + CONTORNO sólido marcado — así cuando
              utilizada es más alta que cosechada (pasa seguido, ver
              hallazgo de Tirian sobre aprovechada>cosechada), su relleno
              ligero NO tapa a cosechada por debajo, y su contorno sigue
              trazando su propia altura con claridad. Ningún par de
              rellenos sólidos semitransparentes resuelve esto — la más
              alta siempre "cubre" a la más chica sin importar el número
              de opacidad; el contorno es lo que garantiza que ambas se
              distingan sin importar cuál sea mayor ese periodo. */}
          <Bar
            yAxisId="vol"
            dataKey="cosechada"
            name="Agua cosechada (L)"
            barSize={OVERLAP_BAR_SIZE}
            fill="#0064d2"
            fillOpacity={0.6}
            radius={[3, 3, 0, 0]}
          />
          <Bar
            yAxisId="vol"
            dataKey="aprovechada"
            name="Agua utilizada (L)"
            barSize={OVERLAP_BAR_SIZE}
            fill="#1abc9c"
            fillOpacity={0.18}
            stroke="#1abc9c"
            strokeWidth={2}
            radius={[3, 3, 0, 0]}
          />
          {/* Agua no medida — tercera barra superpuesta (mismo barSize y
              barGap negativo que las otras dos). Relleno anaranjado
              semitransparente + contorno, para que cosechada y utilizada
              sigan distinguiéndose por debajo. */}
          <Bar
            yAxisId="vol"
            dataKey="noMedida"
            name="Agua no medida (L)"
            barSize={OVERLAP_BAR_SIZE}
            fill={UNMETERED_COLOR}
            fillOpacity={0.45}
            stroke={UNMETERED_COLOR}
            strokeWidth={2}
            radius={[3, 3, 0, 0]}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <ChartLegend items={legendBottom} />
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════
   MINI-GRÁFICA — Nivel del tanque, promedio por periodo
   Vive ENTRE las dos bandas de TwoBandChart, sin alterar sus tamaños
   (150px / 230px intactos) — tratamiento compacto, similar en espíritu
   al card de "Estado del sistema" (una franja angosta, no una gráfica
   grande). Usa tankAvgPct que ya viene calculado del backend: promedio
   de todas las lecturas reales de nivel dentro de cada uno de los 48
   periodos (con lecturas cada pocos minutos, equivale en la práctica al
   "promedio cada 5 min" que pidió Fernando).
   ════════════════════════════════════════════════════════════════════ */
const MiniTankChart = ({ data, tankCapacity }) => {
  // Nivel REAL: solo cuando hay lecturas 'nivel' de verdad ese periodo
  // (tankAvgPct, calculado en el backend). Nivel INFERIDO: para los
  // periodos sin lectura real, usamos tankStart/tankEnd — que el backend
  // YA arrastra/interpola (interpolateGaps) — convertidos a % con la
  // capacidad del tanque. Así no quedan huecos en blanco: se ve el
  // último nivel conocido hasta que cambie (o una rampa suave si hay
  // datos reales a ambos lados del hueco), pero SIEMPRE distinguible de
  // una medición real — línea punteada, no sólida.
  const d2 = data.map((d, i, arr) => {
    const hasReal = d.tankAvgPct != null;
    let inferido = null;
    if (!hasReal && tankCapacity > 0) {
      const start = d.tankStart ?? d.tankEnd ?? null;
      const end = d.tankEnd ?? d.tankStart ?? null;
      if (start != null && end != null) {
        const avgL = (start + end) / 2;
        inferido = Math.min(100, Math.max(0, Math.round((avgL / tankCapacity) * 1000) / 10));
      }
    }
    return {
      label: d.label,
      pct: hasReal ? d.tankAvgPct : null,
      pctInferido: inferido,
      hasAny: hasReal || inferido != null,
    };
  });

  // Puentea el punto real inmediato a cada lado de una racha inferida,
  // para que la línea punteada se conecte visualmente con la sólida en
  // vez de quedar flotando sin tocarla.
  const d3 = d2.map((d, i) => {
    if (d.pctInferido != null) return d;
    const prevInferido = d2[i - 1]?.pctInferido != null;
    const nextInferido = d2[i + 1]?.pctInferido != null;
    if (d.pct != null && (prevInferido || nextInferido)) {
      return { ...d, pctInferido: d.pct };
    }
    return d;
  });

  // Huecos totalmente sin dato ni siquiera para inferir (dispositivo
  // nunca reportó nivel en ese tramo) — sombreado gris, la excepción que
  // sigue sin poder "inventarse".
  const unknownGaps = computeRuns(d3, (d) => !d.hasAny);
  const hasAnyLevel = d3.some((d) => d.hasAny);

  return (
    <div className="db-minitank">
      <div className="db-band-label-row" style={{ marginBottom: "0.1em" }}>
        <span
          className="db-band-ytitle db-band-ytitle--left"
          style={{ color: "#0053ae" }}
        >
          🛢 Nivel del tanque (%)
        </span>
        <span
          className="db-band-ytitle db-band-ytitle--right"
          style={{ color: "#9aabb8" }}
        >
          Promedio por periodo
        </span>
      </div>
      <ResponsiveContainer width="100%" height={90}>
        <ComposedChart
          data={d3}
          syncId="db-band"
          margin={{ top: 4, right: 52, bottom: 4, left: 48 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eaecf4" />
          {unknownGaps.map((g, i) => (
            <ReferenceArea
              key={`minitank-gap-${i}`}
              yAxisId="pct"
              x1={g.x1}
              x2={g.x2}
              y1={0}
              y2={100}
              fill="rgba(200,215,235,0.25)"
              stroke="none"
            />
          ))}
          <XAxis
            dataKey="label"
            tick={false}
            tickLine={false}
            axisLine={{ stroke: "#dde5f0" }}
            height={6}
          />
          <YAxis
            yAxisId="pct"
            domain={[0, 100]}
            ticks={[0, 50, 100]}
            tickFormatter={(v) => `${v}%`}
            tick={{ fontSize: 10, fill: "#7a8a9e" }}
            tickLine={false}
            axisLine={false}
            width={48}
          />
          {/* Eje derecho invisible — mismo motivo que en la gráfica de
              Litros: reserva los mismos 48px que el eje real de "Oferta
              pluvial" en la gráfica de arriba, para que las 3 gráficas
              sincronizadas alineen verticalmente. No usar "hide". */}
          <YAxis
            yAxisId="pctRight"
            orientation="right"
            width={48}
            tick={false}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            formatter={(val, name, props) => {
              if (props?.dataKey === "pctInferido")
                return [val != null ? `${val}% (inferido)` : "Sin datos", "Nivel"];
              return [val != null ? `${val}%` : "Sin datos", "Nivel"];
            }}
          />
          <Line
            yAxisId="pct"
            type="monotone"
            dataKey="pct"
            name="Nivel real (%)"
            stroke="#0053ae"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 3, strokeWidth: 0 }}
            connectNulls={false}
          />
          <Line
            yAxisId="pct"
            type="monotone"
            dataKey="pctInferido"
            name="Nivel inferido (%)"
            stroke="#f5a623"
            strokeWidth={1.5}
            strokeDasharray="4 3"
            dot={false}
            activeDot={{ r: 3, strokeWidth: 0, fill: "#f5a623" }}
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <ChartLegend
        items={[
          { label: "Nivel real (%)", color: "#0053ae" },
          { label: "Nivel inferido (sin lectura ese periodo)", color: "#f5a623", dashed: true },
        ]}
      />

      {!hasAnyLevel && (
        <div className="db-minitank-empty">Sin lecturas de nivel en este rango</div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════
   CHART 2 — OFERTA vs COSECHA
   ════════════════════════════════════════════════════════════════════ */
const OfertaChart = ({ data }) => {
  const d2 = data.map((d) => {
    const hasData = d.hasData !== false;
    return {
      label: d.label,
      hasData,
      cosechada: fmt0(d.rainCaptured || d.captured || 0),
      gap: fmt0(
        Math.max(
          0,
          (d.rainOffered || 0) - (d.rainCaptured || d.captured || 0),
        ),
      ),
      // null si no hay datos, para que la línea de lluvia no "toque cero"
      // durante los huecos sombreados
      lluvia: hasData ? parseFloat(d.rainfall_mm || 0) : null,
    };
  });

  const unknownGaps = computeUnknownGaps(d2);
  // El número de categorías ya NO es fijo (>=48, puede ser más) — el
  // intervalo se adapta para mostrar siempre ~12 etiquetas visibles.
  const TICK_INTERVAL = Math.max(1, Math.floor(d2.length / 12));

  const legend = [
    { label: "Agua cosechada (L)", color: "#0064d2" },
    { label: "Agua perdida (L)", color: "rgba(100,181,246,0.9)" },
    { label: "Lluvia (mm)", color: "#1565c0" },
  ];

  return (
    <>
      <ResponsiveContainer width="100%" height={380}>
        <ComposedChart
          data={d2}
          barCategoryGap="0%"
          barGap={0}
          margin={{ top: 8, right: 52, bottom: 20, left: 48 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#eaecf4"
          />
          {unknownGaps.map((g, i) => (
            <ReferenceArea
              key={`gap-oferta-${i}`}
              yAxisId="L"
              x1={g.x1}
              x2={g.x2}
              fill="rgba(200,215,235,0.22)"
              stroke="none"
            />
          ))}
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: "#7a8a9e" }}
            tickLine={false}
            axisLine={{ stroke: "#dde5f0" }}
            height={30}
            interval={TICK_INTERVAL}
          />
          <YAxis
            yAxisId="L"
            tickFormatter={(v) => `${v}L`}
            tick={{ fontSize: 11, fill: "#7a8a9e" }}
            tickLine={false}
            axisLine={false}
            width={48}
          />
          <YAxis
            yAxisId="mm"
            orientation="right"
            tickFormatter={(v) => `${v}mm`}
            tick={{ fontSize: 11, fill: "#1565c0" }}
            tickLine={false}
            axisLine={false}
            width={48}
          />
          <Tooltip />
          <Bar
            yAxisId="L"
            dataKey="cosechada"
            name="Agua cosechada (L)"
            stackId="r"
            fill="#0064d2"
            radius={[3, 3, 0, 0]}
          />
          <Bar
            yAxisId="L"
            dataKey="gap"
            name="Agua perdida (L)"
            stackId="r"
            fill="rgba(100,181,246,0.45)"
          />
          <Line
            yAxisId="mm"
            type="linear"
            dataKey="lluvia"
            name="Lluvia (mm)"
            stroke="#1565c0"
            strokeWidth={2.5}
            dot={{ r: 3, fill: "#1565c0", strokeWidth: 0 }}
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <ChartLegend items={legend} />
    </>
  );
};

/* ════════════════════════════════════════════════════════════════════
   DASHBOARD A
   ════════════════════════════════════════════════════════════════════ */
const DashboardA = ({ onSwitchDashboard } = {}) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: todayYMD(),
    end: todayYMD(),
  });
  const [summary, setSummary] = useState(null);
  const [reading, setReading] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [chartView, setChartView] = useState("tanque");
  const [chartSeries, setChartSeries] = useState([]);
  const [chartMeta, setChartMeta] = useState(null); // { bucketMinutes, numBuckets } — granularidad EXACTA que eligió el backend
  const [refreshTick, setRefreshTick] = useState(0);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [latestKnown, setLatestKnown] = useState(null);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showEffModal, setShowEffModal] = useState(false);
  // Último evento de lluvia REAL — independiente del rango seleccionado.
  // NO confundir con `summary` (resumen agregado del período elegido).
  const [lastRainEvent, setLastRainEvent] = useState(null);

  useEffect(() => {
    document.body.classList.add("home-page");
    return () => document.body.classList.remove("home-page");
  }, []);

  /* Carga dispositivos */
  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    setLoadingDevices(true);
    fetch(
      `${API_BASE}/api/getDashboardDevices?id_user=${user.id_user}&user_type=${user.type}`,
      { signal: controller.signal },
    )
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.devices?.length) {
          setDevices(d.devices);
          setSelectedDevice(d.devices[0]);
        }
      })
      .catch((e) => {
        if (e.name !== "AbortError") {
          /* noop */
        }
      })
      .finally(() => setLoadingDevices(false));
    return () => controller.abort();
  }, [user]);

  /* Auto-refresh — antes cada 15 s, ahora cada 60 s (menos carga de red y
     de BD sin perder frescura práctica para un dashboard de monitoreo). */
  useEffect(() => {
    const iv = setInterval(() => setRefreshTick((t) => t + 1), 60_000);
    return () => clearInterval(iv);
  }, []);

  /* Carga datos del dashboard — depende de valores primitivos de fecha,
     no del objeto dateRange, para evitar refetch en cada render que no
     cambia realmente el rango (fuente de peticiones duplicadas). */
  useEffect(() => {
    if (!selectedDevice || !dateRange.start) return;
    const controller = new AbortController();
    if (!summary && !reading) setLoadingData(true);
    const id = selectedDevice.id_device,
      { start, end } = dateRange;
    Promise.all([
      fetch(
        `${API_BASE}/api/dashboard/summary?id_device=${id}&dateStart=${start}&dateEnd=${end}`,
        { signal: controller.signal },
      ).then((r) => r.json()),
      fetch(
        `${API_BASE}/api/dashboard/latestReading?id_device=${id}&dateStart=${start}&dateEnd=${end}`,
        { signal: controller.signal },
      ).then((r) => r.json()),
      fetch(
        `${API_BASE}/api/dashboard/alerts?id_device=${id}&dateStart=${start}&dateEnd=${end}`,
        { signal: controller.signal },
      ).then((r) => r.json()),
    ])
      .then(([sData, rData, aData]) => {
        setSummary(sData.success ? sData.summary : null);
        setReading(rData.success ? rData.reading : null);
        setAlerts(aData.success ? aData.alerts : []);
      })
      .catch((e) => {
        if (e.name !== "AbortError") {
          /* noop */
        }
      })
      .finally(() => setLoadingData(false));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice, dateRange.start, dateRange.end, refreshTick]);

  /* Carga chart — SIEMPRE 48 periodos, el backend recalcula su duración
     según [start,end]. Ya no se manda "resolution" en la query string. */
  useEffect(() => {
    if (!selectedDevice || !dateRange.start) return;
    const controller = new AbortController();
    const { start, end } = dateRange,
      id = selectedDevice.id_device;
    setChartSeries(buildPlaceholderSeries()); // placeholder inmediato
    setChartMeta(null);
    fetch(
      `${API_BASE}/api/dashboard/chart?id_device=${id}&dateStart=${start}&dateEnd=${end}`,
      { signal: controller.signal },
    )
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.series?.length) {
          setChartSeries(d.series);
          setChartMeta({ bucketMinutes: d.bucketMinutes, numBuckets: d.numBuckets });
        }
      })
      .catch((e) => {
        if (e.name !== "AbortError") {
          /* noop */
        }
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice, dateRange.start, dateRange.end, refreshTick]);

  /* Estado de conexión — basado en el último latido (keepalive), NO en el rango
     de fechas. Se refresca en el mismo tick de 60 s. */
  useEffect(() => {
    if (!selectedDevice) return;
    const controller = new AbortController();
    const id = selectedDevice.id_device;
    fetch(`${API_BASE}/api/dashboard/deviceStatus?id_device=${id}`, {
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setDeviceStatus(d);
      })
      .catch((e) => {
        if (e.name !== "AbortError") {
          /* noop */
        }
      });
    return () => controller.abort();
  }, [selectedDevice, refreshTick]);

  /* Última lectura conocida — sin filtro de fecha. Se usa como fallback
   cuando el dispositivo está offline o el rango seleccionado no tiene datos. */
  useEffect(() => {
    if (!selectedDevice) return;
    const controller = new AbortController();
    fetch(
      `${API_BASE}/api/dashboard/latestKnownReading?id_device=${selectedDevice.id_device}`,
      { signal: controller.signal },
    )
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setLatestKnown(d.reading);
      })
      .catch((e) => {
        if (e.name !== "AbortError") {
          /* noop */
        }
      });
    return () => controller.abort();
  }, [selectedDevice, refreshTick]);

  /* Último evento de lluvia REAL — sin filtro de fecha (como latestKnownReading). */
  useEffect(() => {
    if (!selectedDevice) return;
    const controller = new AbortController();
    fetch(
      `${API_BASE}/api/dashboard/lastRainEvent?id_device=${selectedDevice.id_device}`,
      { signal: controller.signal },
    )
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setLastRainEvent(d.event);
      })
      .catch((e) => {
        if (e.name !== "AbortError") {
          /* noop */
        }
      });
    return () => controller.abort();
  }, [selectedDevice, refreshTick]);

  /* Eliminar alerta */
  const handleDeleteAlert = (id_alert) => {
    fetch(`${API_BASE}/api/dashboard/alerts/${id_alert}`, {
      method: "DELETE",
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success)
          setAlerts((prev) => prev.filter((a) => a.id_alert !== id_alert));
      })
      .catch(() => {});
  };

  /* Filtros rápidos de fecha */
  const applyQuickRange = (type) => {
    const today = new Date();
    const todayStr = todayYMD();
    if (type === "hoy") {
      setDateRange({ start: todayStr, end: todayStr });
    } else if (type === "7dias") {
      const s = new Date(today);
      s.setDate(s.getDate() - 6);
      setDateRange({ start: toYMD(s), end: todayStr });
    } else if (type === "mes") {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateRange({ start: toYMD(s), end: todayStr });
    } else if (type === "ano") {
      const s = new Date(today.getFullYear(), 0, 1);
      setDateRange({ start: toYMD(s), end: todayStr });
    }
  };

  /* ── Derivados ──────────────────────────────────────────────────── */
  const displayReading = reading ?? latestKnown;
  const isStale = deviceStatus?.status !== "online" && !!displayReading;

  const isBasicPlan = (selectedDevice?.type ?? 0) === 0;

  const opState = displayReading?.operational_state || null;
  const hasFault = displayReading?.fault_flag === 1;
  const tankLevel =
    displayReading?.tank_level_pct != null
      ? parseFloat(displayReading.tank_level_pct)
      : null;
  const tankVol =
    displayReading?.tank_volume_l != null
      ? parseFloat(displayReading.tank_volume_l)
      : null;
  // NOTA: tankCapacity ya no se usa en TwoBandChart — el eje/línea de
  // nivel del tanque se movió por completo a MiniTankChart (que usa % en
  // vez de litros absolutos, no necesita la capacidad). Se deja declarada
  // por si algún consumo futuro la necesita.
  const tankCapacity = selectedDevice?.tank_capacity_l
    ? parseFloat(selectedDevice.tank_capacity_l)
    : 0;
  const numTanques = selectedDevice?.num_tanques
    ? parseInt(selectedDevice.num_tanques, 10)
    : 1;

  const bucketDuration = chartMeta
    ? formatBucketMinutes(chartMeta.bucketMinutes)
    : bucketDurationLabel(dateRange.start, dateRange.end);

  const numDays = Math.max(
    1,
    Math.round(
      (new Date(dateRange.end + "T12:00:00") -
        new Date(dateRange.start + "T12:00:00")) /
        86400000,
    ) + 1,
  );
  const totalDelivered = summary?.delivered_water_l || 0;
  const avgDailyConsumption = totalDelivered / numDays;
  const autonomia =
    totalDelivered >= 1 &&
    avgDailyConsumption > 0 &&
    tankVol != null &&
    tankVol > 0
      ? fmt1(tankVol / avgDailyConsumption)
      : null;

  // El relleno del tanque ahora es SIEMPRE azul sólido (sin degradado ni
  // cambio de color por nivel) — el indicador de nivel pasó al CONTORNO
  // del tanque: verde a partir de 80% de llenado, rojo por debajo de 20%,
  // sin pintar nada (contorno neutro) en el rango intermedio "estable".
  const tankOutlineClass =
    tankLevel == null
      ? ""
      : tankLevel >= 80
        ? "db-tank-body--high"
        : tankLevel < 20
          ? "db-tank-body--low"
          : "";

  const activeStateKeys = new Set();
  if (opState && OP_STATES_B.some((s) => s.key === opState)) {
    activeStateKeys.add(opState);
  }

  const activeIdxList = [...activeStateKeys]
    .map((k) => OP_STATES_B.findIndex((s) => s.key === k))
    .filter((i) => i >= 0);
  const minActiveIdx = activeIdxList.length ? Math.min(...activeIdxList) : -1;
  const maxActiveIdx = activeIdxList.length ? Math.max(...activeIdxList) : -1;

  const ALERT_LABELS = {
    rebose_tanque: {
      titulo: "Tanque rebosando",
      msg: "Usa o transfiere agua para liberar capacidad.",
      icon: "♻️",
      sev: "warn",
    },
    rebose_filtro: {
      titulo: "Filtro rebosando",
      msg: "Posible obstrucción del filtro del techo. Requiere revisión.",
      icon: "🔧",
      sev: "warn",
    },
    falla_secuencia_wps: {
      titulo: "Falla de secuencia de sensores",
      msg: "Lectura de sensores inconsistente. Revisa la instalación de los WPS.",
      icon: "⚠️",
      sev: "warn",
    },
    fuga_candidata: {
      titulo: "Posible fuga detectada",
      msg: "El nivel del tanque bajó sin consumo registrado.",
      icon: "⚠️",
      sev: "crit",
    },
    ingreso_no_pluvial: {
      titulo: "Ingreso de agua no pluvial detectado",
      msg: "Se registró entrada de agua sin lluvia activa.",
      icon: "💧",
      sev: "info",
    },
  };
  const translateAlert = (a) => {
    const key = a.alert_type?.toLowerCase().replace(/\s+/g, "_");
    return (
      ALERT_LABELS[key] || {
        titulo: a.alert_type,
        msg: a.status === "active" ? "Alerta activa." : "Alerta resuelta.",
        icon: a.severity === "critical" ? "🔴" : "🟡",
        sev: a.severity === "critical" ? "crit" : "warn",
      }
    );
  };

  const isEventOutOfRange =
    !!lastRainEvent &&
    (lastRainEvent.date < dateRange.start || lastRainEvent.date > dateRange.end);

  const lastEvent = {
    date: lastRainEvent?.date || "—",
    time: lastRainEvent?.time || "",
    mm: fmt0(lastRainEvent?.rainfall_mm || 0),
    offered: fmt0(lastRainEvent?.offered_rain_l || 0),
    cosechada: fmt0(lastRainEvent?.rain_captured_l || 0),
    aprovechada: fmt0(lastRainEvent?.delivered_water_l || 0),
    eficiencia: fmt0(lastRainEvent?.capture_efficiency_pct || 0),
    no_aprovechada: fmt0(lastRainEvent?.unharvested_l || 0),
    finalState: lastRainEvent?.operational_state || null,
  };

  // Eficiencia de cosecha del período — el card solo es interactivo (con
  // sugerencia tipo venta) cuando está por debajo de 50%.
  const effPct = fmt0(summary?.capture_efficiency_pct || 0);
  const effIsLow = (summary?.offered_rain_volume_l || 0) > 0 && effPct < 50;

  const MODULE_ROUTES = {
    dashboard: "/home",
    users: "/users",
    devices: "/devices",
  };

  /* ── Render ──────────────────────────────────────────────────────── */
  return (
    <div className="db-wrapper">
      <Topbar
        devices={devices}
        selectedDevice={selectedDevice}
        onDeviceChange={setSelectedDevice}
        reading={reading}
        deviceStatus={deviceStatus}
        userType={user.type}
        loadingDevices={loadingDevices}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        alerts={alerts}
        onNavigate={(key) => navigate(MODULE_ROUTES[key] || "/home")}
      />

      {loadingData && !reading ? (
        <div className="db-loading">
          <div className="db-spinner" />
          <span>Cargando datos…</span>
        </div>
      ) : (
        <div className="db-body">
          {/* ════ COLUMNA IZQUIERDA ════ */}
          <aside className="db-left">
            <div className="db-agua-header">
              <span className="db-agua-title">AGUA DISPONIBLE</span>
              <span
                className="db-info-icon"
                title="Volumen actual almacenado en el tanque"
              >
                ⓘ
              </span>
            </div>
            <div className="db-agua-hero">
              {tankVol != null ? fmt0(tankVol) : "—"}{" "}
              <span className="db-agua-unit">L</span>
            </div>
            <div className="db-agua-pct">
              {tankLevel != null
                ? `${fmt0(tankLevel)}% del tanque`
                : "Sin datos"}
            </div>

            <div className="db-tank-wrap">
              <div className="db-tank-marks">
                {[100, 75, 50, 25, 0].map((m) => (
                  <span
                    key={m}
                    className="db-tank-mark"
                    style={{ top: `${100 - m}%` }}
                  >
                    {m}%
                  </span>
                ))}
              </div>
              <div className={`db-tank-body ${tankOutlineClass}`}>
                <div
                  className="db-tank-fill"
                  style={{ height: `${tankLevel ?? 0}%` }}
                />
                <div className="db-tank-pct-label">
                  {tankLevel != null ? `${fmt0(tankLevel)}%` : "—"}
                </div>
              </div>
            </div>

            <div className="db-autonomia-badge">
              <span className="db-autonomia-icon">📅</span>
              <div>
                <div className="db-autonomia-label">Autonomía estimada</div>
                <div className="db-autonomia-val">
                  {autonomia ? `${autonomia} días` : "—"}
                  {!autonomia && (
                    <span className="db-autonomia-hint">
                      {" "}
                      (sin datos de consumo)
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="db-estado-actual">
              <span
                className="db-estado-dot"
                style={{ background: getEstadoColor(opState) }}
              />
              <span className="db-estado-text">{stateLabel(opState)}</span>
            </div>

            {opState && (
              <div className="db-siguiente-accion">
                <span className="db-sa-icon">💡</span>
                <div>
                  <div className="db-sa-label">Siguiente acción</div>
                  <div className="db-sa-msg">
                    {getSiguienteAccion(opState, tankLevel)}
                  </div>
                </div>
              </div>
            )}

            <div className="db-resumen-title">RESUMEN DEL PERÍODO</div>
            <div className="db-resumen-list">
              {[
                {
                  icon: "🌧",
                  label: "Lluvia",
                  val: `${fmt0(summary?.rainfall_mm || 0)} mm`,
                },
                {
                  icon: "☁️",
                  label: "Oferta pluvial",
                  val: `${fmt0(summary?.offered_rain_volume_l || 0)} L`,
                },
                {
                  icon: "💧",
                  label: "Agua cosechada",
                  val: `${fmt0(summary?.rain_captured_l || 0)} L`,
                },
                {
                  icon: "🚿",
                  label: "Agua utilizada",
                  val: `${fmt0(summary?.delivered_water_l || 0)} L`,
                },
                {
                  icon: "🟠",
                  label: "Agua no medida",
                  val: `${fmt0(summary?.unmetered_water_l || 0)} L`,
                },
              ].map(({ icon, label, val }) => (
                <div key={label} className="db-resumen-item">
                  <span className="db-resumen-icon">{icon}</span>
                  <span className="db-resumen-label">{label}</span>
                  <span className="db-resumen-val">{val}</span>
                </div>
              ))}
            </div>
          </aside>

          {/* ════ COLUMNA CENTRAL ════ */}
          <main className="db-center">
            {/* Estado del sistema */}
            <div className="db-estado-bar card">
              <span className="db-eb-title">ESTADO DEL SISTEMA</span>
              <div className="db-estado-steps">
                {OP_STATES_B.map((s, i) => {
                  const isActive = activeStateKeys.has(s.key);
                  const isOverflow =
                    s.key === "Tank Overflow" && isActive && !hasFault;
                  const isPast = !isActive && i < minActiveIdx;

                  const isExemptFromLock = s.key === "First Flush Active";
                  const isLocked = isBasicPlan && !isExemptFromLock;

                  const cls = `db-estep${
                    isLocked
                      ? " db-estep--locked"
                      : isActive && hasFault
                        ? " db-estep--fault"
                        : isActive && isOverflow
                          ? " db-estep--overflow"
                          : isActive
                            ? " db-estep--active"
                            : isPast
                              ? " db-estep--past"
                              : ""
                  }`;
                  const lineFilled = i < maxActiveIdx;
                  return (
                    <React.Fragment key={s.key}>
                      <div className={cls} title={s.label}>
                        <div className="db-estep-icon">
                          {s.icon}
                          {isLocked && (
                            <span
                              className="db-estep-lock"
                              title="Disponible en el plan Premium"
                            >
                              🔒
                            </span>
                          )}
                        </div>
                        <div className="db-estep-label">{s.label}</div>
                      </div>
                      {i < OP_STATES_B.length - 1 && (
                        <div
                          className={`db-estep-line${lineFilled ? " db-estep-line--filled" : ""}`}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              {isBasicPlan && (
                <button
                  className="db-eb-unlock-link"
                  onClick={() => setShowUnlockModal(true)}
                >
                  🔓 Desbloquear Sistema Completo
                </button>
              )}
            </div>

            {/* Gráfica principal */}
            <div className="db-chart-card card">
              <div className="db-chart-top">
                <div className="db-chart-top-left">
                  <div className="db-chart-tabs">
                    <button
                      className={`db-chart-tab${chartView === "tanque" ? " db-chart-tab--active" : ""}`}
                      onClick={() => setChartView("tanque")}
                    >
                      Tanque + lluvia
                    </button>
                    <button
                      className={`db-chart-tab${chartView === "oferta" ? " db-chart-tab--active" : ""}`}
                      onClick={() => setChartView("oferta")}
                    >
                      Oferta vs cosecha
                    </button>
                    {isStale && displayReading && (
                      <div className="db-stale-inline">
                        <span>⚠️</span>
                        <span>
                          Sin conexión · Última lectura:{" "}
                          {displayReading.reading_date}{" "}
                          {displayReading.reading_time}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="db-quick-filters">
                    <button onClick={() => applyQuickRange("hoy")}>
                      Hoy
                    </button>
                    <button onClick={() => applyQuickRange("7dias")}>
                      Últ. 7 días
                    </button>
                    <button onClick={() => applyQuickRange("mes")}>
                      Mes
                    </button>
                    <button onClick={() => applyQuickRange("ano")}>
                      Año
                    </button>
                  </div>

                  <div className="db-chart-meta">
                    <span>
                      Área de captación: {selectedDevice?.roof_area_m2 || "—"}{" "}
                      m²
                    </span>
                    <span>·</span>
                    <span>1 mm en 1 m² = 1 L</span>
                    {numTanques > 1 && (
                      <>
                        <span>·</span>
                        <span>{numTanques} tanques conectados</span>
                      </>
                    )}
                  </div>
                </div>
                <span className="db-res-badge">{bucketDuration}</span>
              </div>

              {chartView === "tanque" ? (
                <TwoBandChart data={chartSeries} tankCapacity={tankCapacity} />
              ) : (
                <OfertaChart data={chartSeries} />
              )}
            </div>

            {/* Contabilidad de agua */}
            <div className="db-contabilidad card">
              <div className="db-cont-title">
                CONTABILIDAD DE AGUA
                <span className="db-cont-period">
                  (
                  {dateRange.start === dateRange.end
                    ? "Hoy"
                    : dateRange.start + " — " + dateRange.end}
                  )
                </span>
              </div>
              <div className="db-cont-grid">
                {[
                  {
                    label: "Oferta pluvial",
                    val: fmt0(summary?.offered_rain_volume_l || 0),
                    color: "#0053ae",
                    icon: "☁️",
                  },
                  {
                    label: "Agua cosechada",
                    val: fmt0(summary?.rain_captured_l || 0),
                    color: "#0064d2",
                    icon: "💧",
                  },
                  {
                    label: "Agua utilizada",
                    val: fmt0(summary?.delivered_water_l || 0),
                    color: "#1abc9c",
                    icon: "🚿",
                  },
                  {
                    label: "Agua no medida",
                    val: fmt0(summary?.unmetered_water_l || 0),
                    color: UNMETERED_COLOR,
                    icon: "🟠",
                  },
                  {
                    label: "Agua perdida",
                    val: fmt0(summary?.unharvested_l || 0),
                    color: "#64b5f6",
                    icon: "♻️",
                  },
                ].map(({ label, val, color, icon }) => (
                  <div key={label} className="db-cont-card">
                    <span className="db-cont-icon" style={{ color }}>
                      {icon}
                    </span>
                    <div className="db-cont-label">{label}</div>
                    <div className="db-cont-val" style={{ color }}>
                      {val.toLocaleString()}{" "}
                      <span className="db-cont-unit">L</span>
                    </div>
                  </div>
                ))}
                <div
                  className={`db-cont-card db-cont-card--eff${effIsLow ? " db-cont-card--clickable" : ""}`}
                  onClick={effIsLow ? () => setShowEffModal(true) : undefined}
                  role={effIsLow ? "button" : undefined}
                  tabIndex={effIsLow ? 0 : undefined}
                >
                  <span className="db-cont-icon" style={{ color: "#8e44ad" }}>
                    📊
                  </span>
                  <div className="db-cont-label">Eficiencia de cosecha</div>
                  <div className="db-cont-val" style={{ color: "#8e44ad" }}>
                    {effPct}
                    <span className="db-cont-unit">%</span>
                  </div>
                  {(summary?.offered_rain_volume_l || 0) > 0 && (
                    <div className="db-cont-sub">
                      {fmt0(summary?.rain_captured_l || 0).toLocaleString()} L
                      cosechados
                      <br />
                      de{" "}
                      {fmt0(
                        summary?.offered_rain_volume_l || 0,
                      ).toLocaleString()}{" "}
                      L ofertados
                    </div>
                  )}
                  {effIsLow && (
                    <div className="db-cont-eff-hint">
                      💡 Ver cómo mejorar →
                    </div>
                  )}
                </div>
                <div className="db-cont-card db-cont-card--pie">
                  <div className="db-cont-label">
                    Distribución de la oferta pluvial
                  </div>
                  {(summary?.offered_rain_volume_l || 0) > 0 ? (
                    <div className="db-cont-pie-inner">
                      <div className="db-cont-pie-chart">
                        <ResponsiveContainer width="100%" height={90}>
                          <PieChart>
                            <Pie
                              data={[
                                {
                                  name: "Cosechada",
                                  value: parseFloat(
                                    summary?.rain_captured_l || 0,
                                  ),
                                },
                                {
                                  name: "Perdida",
                                  value: parseFloat(
                                    summary?.unharvested_l || 0,
                                  ),
                                },
                              ].filter((d) => d.value > 0)}
                              cx="50%"
                              cy="50%"
                              innerRadius={28}
                              outerRadius={44}
                              paddingAngle={3}
                              dataKey="value"
                            >
                              {[0, 1].map((i) => (
                                <Cell key={i} fill={PIE_COLORS[i]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v) => [`${fmt0(v)} L`]} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="db-cont-pie-legend">
                        <span>
                          <span
                            className="db-pie-dot"
                            style={{ background: PIE_COLORS[0] }}
                          />{" "}
                          Cosechada: {fmt0(summary?.rain_captured_l || 0)} L
                        </span>
                        <span>
                          <span
                            className="db-pie-dot"
                            style={{ background: PIE_COLORS[1] }}
                          />{" "}
                          Perdida: {fmt0(summary?.unharvested_l || 0)} L
                        </span>
                        <span className="db-cont-pie-total">
                          Total:{" "}
                          {fmt0(
                            summary?.offered_rain_volume_l || 0,
                          ).toLocaleString()}{" "}
                          L
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="db-cont-empty">
                      Sin oferta pluvial en el período
                    </div>
                  )}
                </div>
              </div>
            </div>
          </main>

          {/* ════ COLUMNA DERECHA ════ */}
          <aside className="db-right">
            {/* Último evento de lluvia */}
            <div className="db-evento card">
              {/* Solo visible para Admin/Donante cuando Home.js les dio a
                  elegir vista — Home.js es la única fuente de verdad sobre
                  quién recibe este prop, no se duplica el chequeo de type
                  aquí. Va DENTRO del card (arriba de todo) para no flotar
                  sobre otro contenido — antes se encimaba con este mismo
                  título al ser position:fixed. */}
              {onSwitchDashboard && (
                <button className="db-switch-link" onClick={onSwitchDashboard}>
                  🔀 Cambiar a Portafolio Institucional
                </button>
              )}
              <div className="db-evento-title">ÚLTIMO EVENTO DE LLUVIA</div>
              {lastEvent.date !== "—" ? (
                <>
                  {isEventOutOfRange && (
                    <div className="db-evento-outofrange">
                      Mostrando el último evento conocido (fuera del rango
                      seleccionado)
                    </div>
                  )}
                  <div className="db-evento-head">
                    <div className="db-evento-date-row">
                      <span className="db-evento-date-icon">📅</span>
                      <div>
                        <div className="db-evento-date-val">
                          {lastEvent.date}
                        </div>
                        <div className="db-evento-date-time">
                          {lastEvent.time}
                        </div>
                      </div>
                    </div>
                    {lastEvent.mm > 0 && (
                      <div className="db-evento-mm-block">
                        <div className="db-evento-mm">{lastEvent.mm}</div>
                        <div className="db-evento-mm-unit">mm</div>
                      </div>
                    )}
                  </div>
                  <div className="db-evento-divider" />
                  <div className="db-evento-metrics">
                    {[
                      {
                        label: "Oferta pluvial",
                        val: lastEvent.offered,
                        color: "#0053ae",
                        unit: "L",
                      },
                      {
                        label: "Agua cosechada",
                        val: lastEvent.cosechada,
                        color: "#0064d2",
                        unit: "L",
                      },
                      {
                        label: "Agua utilizada",
                        val: lastEvent.aprovechada,
                        color: "#1abc9c",
                        unit: "L",
                      },
                      {
                        label: "Eficiencia de cosecha",
                        val: lastEvent.eficiencia,
                        color: "#8e44ad",
                        unit: "%",
                      },
                      {
                        label: "Agua perdida",
                        val: lastEvent.no_aprovechada,
                        color: "#64b5f6",
                        unit: "L",
                      },
                    ].map(({ label, val, color, unit }) => (
                      <div key={label} className="db-evento-metric">
                        <span className="db-evento-metric-label">{label}</span>
                        <span
                          className="db-evento-metric-val"
                          style={{ color }}
                        >
                          {typeof val === "number" ? val.toLocaleString() : val}
                          <span className="db-evento-metric-unit">{unit}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="db-evento-divider" />
                  {lastEvent.finalState && (
                    <div className="db-evento-estado-row">
                      <span className="db-evento-estado-label">
                        Estado final
                      </span>
                      <span
                        className={`db-evento-estado-badge db-estado-badge--${lastEvent.finalState === "Tank Overflow" ? "overflow" : lastEvent.finalState === "Harvesting Active" ? "active" : "neutral"}`}
                      >
                        {stateLabel(lastEvent.finalState)}
                      </span>
                    </div>
                  )}
                  <div className="db-evento-insight">
                    <span className="db-evento-insight-icon">💡</span>
                    <div>
                      <div className="db-evento-insight-title">Insight</div>
                      <div className="db-evento-insight-msg">
                        {getSiguienteAccion(lastEvent.finalState, tankLevel)}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="db-evento-empty">
                  Sin eventos registrados para este período
                </div>
              )}
            </div>

            {/* Alertas */}
            <div className="db-alertas card">
              <div className="db-alertas-header">
                <span className="db-alertas-title">ALERTAS</span>
                {alerts.filter((a) => a.status === "active").length > 0 && (
                  <span className="db-alertas-count">
                    {alerts.filter((a) => a.status === "active").length} activas
                  </span>
                )}
              </div>
              {alerts.length === 0 ? (
                <div className="db-alertas-empty">
                  Sin alertas para este período
                </div>
              ) : (
                <div className="db-alertas-list">
                  {alerts.map((a, i) => {
                    const t = translateAlert(a);
                    return (
                      <div
                        key={a.id_alert || i}
                        className={`db-alerta-item db-alerta-item--${t.sev}`}
                      >
                        <span className="db-alerta-icon">{t.icon}</span>
                        <div className="db-alerta-body">
                          <div className="db-alerta-titulo">{t.titulo}</div>
                          <div className="db-alerta-msg">{t.msg}</div>
                          <div className="db-alerta-time">{a.triggered_at}</div>
                        </div>
                        <button
                          className="db-alerta-del"
                          title="Eliminar alerta"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleDeleteAlert(a.id_alert)}
                        >
                          🗑
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>
        </div>
      )}

      {/* Modal — Desbloquear Sistema Completo (upsell Premium) */}
      {showUnlockModal && (
        <div
          className="db-unlock-overlay"
          onClick={() => setShowUnlockModal(false)}
        >
          <div
            className="db-unlock-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="db-unlock-close"
              onClick={() => setShowUnlockModal(false)}
              aria-label="Cerrar"
            >
              ×
            </button>
            <div className="db-unlock-badge">PREMIUM</div>
            <div className="db-unlock-icon">🔓</div>
            <h3 className="db-unlock-title">
              Desbloquea todo el potencial de tu sistema
            </h3>
            <p className="db-unlock-text">
              Con el plan <strong>Premium</strong> monitoreas cada etapa del
              proceso de cosecha en tiempo real: detección de lluvia, primer
              lavado, cosecha activa, rebose y más — sin restricciones.
            </p>
            <a className="db-unlock-cta" href="tel:+529999999999">
              📞 Contáctanos: +52 1 99 9999 9999
            </a>
            <div className="db-unlock-brand">Neta Cero</div>
          </div>
        </div>
      )}

      {/* Modal — Mejora tu eficiencia de cosecha (upsell, <50%) */}
      {showEffModal && (
        <div
          className="db-unlock-overlay"
          onClick={() => setShowEffModal(false)}
        >
          <div
            className="db-unlock-modal db-effmodal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="db-unlock-close"
              onClick={() => setShowEffModal(false)}
              aria-label="Cerrar"
            >
              ×
            </button>
            <div className="db-unlock-badge db-effmodal-badge">
              MEJORA TU SISTEMA
            </div>
            <div className="db-unlock-icon">📈</div>
            <h3 className="db-unlock-title">
              Tu eficiencia de cosecha está en {effPct}%
            </h3>
            <p className="db-unlock-text">
              Una eficiencia baja normalmente significa que tu sistema está
              limitado por capacidad de almacenamiento o superficie de
              captación — llueve más de lo que hoy puedes aprovechar. Platica
              con nuestro equipo sobre <strong>aumentar tu tanque</strong> o{" "}
              <strong>tu área de captación</strong> para cosechar más agua de
              lluvia.
            </p>
            <a className="db-unlock-cta" href="tel:+529999999999">
              📞 Contáctanos: +52 1 99 9999 9999
            </a>
            <div className="db-unlock-brand">Neta Cero</div>
          </div>
        </div>
      )}

      {/* Asistente de chat — solo administradores (el widget se oculta solo) */}
      <ChatWidget />
    </div>
  );
};

export default DashboardA;